import { useEffect, useState, useCallback } from "react";
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from "recharts";
import { Loader } from "lucide-react";
import { useTranslation } from "react-i18next";
import api from "../../lib/api";

type Range = "7d" | "30d" | "90d";

interface FlowPoint { date: string; validated: number; pending: number; }

const VALIDATED = "#18181B";
const PENDING = "#A1A1AA";

/** Kompakt beløpsformat for akse/tooltip (valuta settes som prefiks av kalleren). */
function compact(n: number): string {
  const abs = Math.abs(n);
  if (abs >= 1_000_000) return `${(n / 1_000_000).toFixed(abs >= 10_000_000 ? 0 : 1)} mill`;
  if (abs >= 1_000) return `${Math.round(n / 1_000)}k`;
  return `${Math.round(n)}`;
}

export default function AreaChartFlow({ projectId, currency = "NOK" }: { projectId?: string; currency?: string }) {
  const { t, i18n } = useTranslation();
  const [range, setRange] = useState<Range>(projectId ? "90d" : "7d");
  const [data, setData] = useState<FlowPoint[]>([]);
  const [loading, setLoading] = useState(true);
  const [hidden, setHidden] = useState<{ validated: boolean; pending: boolean }>({
    validated: false, pending: false,
  });

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api.get(`/portfolio/invoice-flow?range=${range}${projectId ? `&project=${projectId}` : ""}`)
      .then(r => { if (!cancelled) setData(r.data.series || []); })
      .catch(() => { if (!cancelled) setData([]); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [range, projectId]);

  const locale = i18n.language?.startsWith("en") ? "en-US" : "nb-NO";
  const fmtDate = useCallback((iso: string) => {
    const d = new Date(iso);
    return d.toLocaleDateString(locale, { day: "numeric", month: "short" });
  }, [locale]);

  const toggle = (k: "validated" | "pending") =>
    setHidden(h => ({ ...h, [k]: !h[k] }));

  const ranges: Range[] = ["7d", "30d", "90d"];
  const hasData = data.some(d => d.validated > 0 || d.pending > 0);

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (!active || !payload?.length) return null;
    return (
      <div className="bg-night text-white rounded-[10px] px-3 py-2 shadow-pop">
        <p className="text-[11px] text-white/60 mb-1">{fmtDate(label)}</p>
        {payload.map((p: any) => (
          <div key={p.dataKey} className="flex items-center gap-2 text-[12px]">
            <span className="w-2 h-2 rounded-full" style={{ background: p.color }} />
            <span className="text-white/70">
              {p.dataKey === "validated" ? t("flow.validated") : t("flow.pending")}
            </span>
            <span className="font-bold tabular ml-auto">{currency} {compact(p.value)}</span>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="cx-card p-5">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-4">
        <div>
          <h3 className="text-[15px] font-bold text-night">{t("flow.title")}</h3>
          <p className="text-[12.5px] text-muted mt-0.5">{t("flow.subtitle")}</p>
        </div>
        <div className="flex items-center gap-3">
          {/* Legend (klikkbar) */}
          <div className="flex items-center gap-3">
            {(["validated", "pending"] as const).map(k => (
              <button
                key={k}
                onClick={() => toggle(k)}
                className={`flex items-center gap-1.5 text-[12px] font-medium transition-opacity ${
                  hidden[k] ? "opacity-40" : "opacity-100"
                }`}
              >
                <span
                  className="w-2.5 h-2.5 rounded-[3px]"
                  style={{ background: k === "validated" ? VALIDATED : PENDING }}
                />
                <span className="text-muted">
                  {k === "validated" ? t("flow.validated") : t("flow.pending")}
                </span>
              </button>
            ))}
          </div>
          {/* Range-velger */}
          <div className="flex bg-canvas border border-line rounded-[9px] p-0.5">
            {ranges.map(r => (
              <button
                key={r}
                onClick={() => setRange(r)}
                className={`px-2.5 py-1 rounded-[6px] text-[11.5px] font-semibold transition-all ${
                  range === r ? "bg-white text-night shadow-sm" : "text-faint hover:text-muted"
                }`}
              >
                {t(`flow.range_${r}`)}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Chart */}
      <div className="h-[260px]">
        {loading ? (
          <div className="h-full flex items-center justify-center">
            <Loader className="w-5 h-5 animate-spin text-faint" />
          </div>
        ) : !hasData ? (
          <div className="h-full flex flex-col items-center justify-center text-center">
            <p className="text-[14px] font-semibold text-night">{t("flow.empty_title")}</p>
            <p className="text-[12.5px] text-muted mt-1">{t("flow.empty_sub")}</p>
          </div>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={data} margin={{ top: 8, right: 8, left: -8, bottom: 0 }}>
              <defs>
                <linearGradient id="gValidated" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={VALIDATED} stopOpacity={0.18} />
                  <stop offset="100%" stopColor={VALIDATED} stopOpacity={0} />
                </linearGradient>
                <linearGradient id="gPending" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={PENDING} stopOpacity={0.22} />
                  <stop offset="100%" stopColor={PENDING} stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#EEEEEE" vertical={false} />
              <XAxis
                dataKey="date" tickFormatter={fmtDate}
                tick={{ fontSize: 11, fill: "#A1A1AA" }}
                axisLine={false} tickLine={false} minTickGap={24}
              />
              <YAxis
                tickFormatter={(v) => compact(v)}
                tick={{ fontSize: 11, fill: "#A1A1AA" }}
                axisLine={false} tickLine={false} width={48}
              />
              <Tooltip content={<CustomTooltip />} cursor={{ stroke: "#D4D4D8", strokeWidth: 1 }} />
              {!hidden.pending && (
                <Area
                  type="monotone" dataKey="pending" stroke={PENDING} strokeWidth={2}
                  fill="url(#gPending)" animationDuration={800}
                  activeDot={{ r: 4, fill: PENDING, stroke: "#fff", strokeWidth: 2 }}
                />
              )}
              {!hidden.validated && (
                <Area
                  type="monotone" dataKey="validated" stroke={VALIDATED} strokeWidth={2.5}
                  fill="url(#gValidated)" animationDuration={800}
                  activeDot={{ r: 4, fill: VALIDATED, stroke: "#fff", strokeWidth: 2 }}
                />
              )}
            </AreaChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  );
}
